<template>
  <a-modal :visible="visible" :title="modalTitle" ok-text="确认" cancel-text="取消" :confirm-loading="loading"
    destroy-on-close :mask-closable="false" @ok="handleOk" @cancel="handleCancel">
    <a-form ref="formRef" :model="formState" :label-col="{ span: 7 }" :wrapper-col="{ span: 15 }" :rules="rules">
      <a-form-item label="活动名称" name="name">
        <a-input v-model:value.trim="formState.name" placeholder="请输入活动名称" />
      </a-form-item>

      <a-form-item label="优惠类型" name="type">
        <a-select v-model:value="formState.type" placeholder="请选择优惠类型" @change="onTypeChange">
          <a-select-option v-for="t in typeOptions" :key="t" :value="t">{{ t }}</a-select-option>
        </a-select>
      </a-form-item>

      <a-form-item :label="conditionLabel" name="conditionValue">
        <a-input-number v-model:value="formState.conditionValue" :min="0" :precision="conditionPrecision"
          :step="conditionStep" style="width: 100%" :placeholder="conditionPlaceholder" @change="onConditionChange" />
      </a-form-item>

      <a-form-item :label="discountLabel" name="discountValue">
        <a-input-number v-model:value="formState.discountValue" :min="0" :precision="discountPrecision"
          :step="discountStep" style="width: 100%" :placeholder="discountPlaceholder" />
        <div v-if="discountTip" class="form-tip">{{ discountTip }}</div>
      </a-form-item>

      <a-form-item label="生效日期" name="startDate">
        <a-date-picker v-model:value="formState.startDate" value-format="YYYY-MM-DD" placeholder="请选择生效日期"
          style="width: 100%" @change="onStartDateChange" />
      </a-form-item>

      <a-form-item label="失效日期" name="endDate">
        <a-date-picker v-model:value="formState.endDate" value-format="YYYY-MM-DD" placeholder="请选择失效日期"
          style="width: 100%" :disabled-date="disabledEndDate" />
      </a-form-item>
    </a-form>
  </a-modal>

</template>

<script>
import { AddPromotion, UpdatePromotion } from "/src/api/shopping.js";


export default {
  name: "优惠管理",
  
  props: {
    visible: { type: Boolean, default: false },
    record: { type: Object, default: () => ({}) },
  },
  emits: ["update:visible", "ok"],
  data() {
    return {
      loading: false,
      typeOptions: ["满件减额", "满额减额", "满额折扣"],
      formState: {
        id: undefined,
        name: "",
        type: undefined,
        conditionValue: undefined,
        discountValue: undefined,
        startDate: undefined,
        endDate: undefined,
        isGlobal: true,
      },
    };
  },
  computed: {
    isEdit() {
      return !!this.formState.id;
    },

    modalTitle() {
      return this.isEdit ? "编辑优惠方案" : "新增优惠方案";
    },
    conditionLabel() {
      return this.formState.type === "满件减额" ? "满足件数" : "满足金额(元)";
    },
    conditionPlaceholder() {
      return this.formState.type === "满件减额" ? "请输入满足件数" : "请输入满足金额";
    },
    discountLabel() {
      return this.formState.type === "满额折扣" ? "折扣系数" : "减免金额(元)";
    },
    discountPlaceholder() {
      return this.formState.type === "满额折扣" ? "例如 0.85 代表 8.5 折" : "请输入减免金额";
    },
    conditionPrecision() {
      return this.formState.type === "满件减额" ? 0 : 2;
    },
    conditionStep() {
      return 1;
    },
    discountPrecision() {
      return this.formState.type === "满额折扣" ? 4 : 2;
    },
    discountStep() {
      return this.formState.type === "满额折扣" ? 0.05 : 1;
    },
    discountTip() {
      return this.formState.type === "满额折扣"
        ? "折扣系数 > 0 且 < 1，例如 0.85 代表 8.5 折"
        : "";
    },
    rules() {
      // 触发条件校验
      const validateCondition = (_rule, value) => {
        if (value === null || value === undefined || value === "") {
          return Promise.reject(`请输入${this.conditionLabel}`);
        }
        const val = Number(value);
        if (Number.isNaN(val) || val <= 0) {
          return Promise.reject("数值必须大于 0");
        }
        if (this.formState.type === "满件减额" && !Number.isInteger(val)) {
          return Promise.reject("件数必须为正整数");
        }
        return Promise.resolve();
      };

      // 优惠值校验
      const validateDiscount = (_rule, value) => {
        if (value === null || value === undefined || value === "") {
          return Promise.reject(`请输入${this.discountLabel}`);
        }
        const val = Number(value);
        if (Number.isNaN(val) || val <= 0) {
          return Promise.reject("数值必须大于 0");
        }
        if (this.formState.type === "满额折扣" && val >= 1) {
          return Promise.reject("折扣系数必须小于 1");
        }
        if (
          this.formState.type === "满额减额" &&
          this.formState.conditionValue !== undefined &&
          val >= Number(this.formState.conditionValue)
        ) {
          return Promise.reject("减免金额必须小于满足金额");
        }
        return Promise.resolve();
      };

      const validateEndDate = (_rule, value) => {
        if (value && this.formState.startDate && value < this.formState.startDate) {
          return Promise.reject("失效日期不能早于生效日期");
        }
        return Promise.resolve();
      };

      return {
        name: [{ required: true, message: "请输入活动名称", trigger: "blur" }],
        type: [{ required: true, message: "请选择优惠类型", trigger: "change" }],
        conditionValue: [{ validator: validateCondition, trigger: ["blur", "change"] }],
        discountValue: [{ validator: validateDiscount, trigger: ["blur", "change"] }],
        endDate: [{ validator: validateEndDate, trigger: "change" }],
      };
    },
  },
  watch: {
    record: {
      handler(val) {
        if (val && val.id) {
          
          this.formState = {
            id: val.id,
            name: val.name || "",
            type: val.type || undefined,
            conditionValue: val.conditionValue,
            discountValue: val.discountValue,
            startDate: val.startDate || undefined,
            endDate: val.endDate || undefined,
            isGlobal: val.isGlobal !== undefined ? !!val.isGlobal : true,
          };

        } else {
          this.resetForm();
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    resetForm() {
      this.formState = {
        id: undefined,
        name: "",
        type: undefined,
        conditionValue: undefined,
        discountValue: undefined,
        startDate: undefined,
        endDate: undefined,
        isGlobal: true,
      };

      this.$refs.formRef?.clearValidate();
    },




    onTypeChange() {
      this.$nextTick(() => {
        if (this.$refs.formRef) {
          this.$refs.formRef.validateFields(["conditionValue", "discountValue"]);
        }
      });
    },

    onConditionChange() {
      if (this.formState.type === "满额减额" && this.formState.discountValue !== undefined) {
        this.$nextTick(() => {
          this.$refs.formRef?.validateFields(["discountValue"]);
        });
      }
    },

    onStartDateChange() {
      this.$nextTick(() => {
        this.$refs.formRef?.validateFields(["endDate"]);
      });
    },

    disabledEndDate(current) {
      if (!this.formState.startDate || !current) {
        return false;
      }
      const startDay = new Date(this.formState.startDate).setHours(0, 0, 0, 0);
      const currentDay = current.toDate ? current.toDate().setHours(0, 0, 0, 0) : new Date(current).setHours(0, 0, 0, 0);
      return currentDay < startDay;
    },

    handleCancel() {
      this.closeModal();
    },

    async handleOk() {
      if (!this.$refs.formRef) return;
      if (document.activeElement && typeof document.activeElement.blur === "function") {
        document.activeElement.blur();
      }
      await this.$nextTick();

      try {
        await this.$refs.formRef.validate();
      } catch (e) {
        return;
      }

      this.loading = true;
      try {
        const round = (n, digits) => Math.round(Number(n) * Math.pow(10, digits)) / Math.pow(10, digits);
        const payload = {
          name: this.formState.name,
          type: this.formState.type,
          isGlobal: !!this.formState.isGlobal,
          conditionValue:
            this.formState.type === "满件减额"
              ? Math.round(Number(this.formState.conditionValue))
              : round(this.formState.conditionValue, 2),
          discountValue:
            this.formState.type === "满额折扣"
              ? round(this.formState.discountValue, 4)
              : round(this.formState.discountValue, 2),
        };

        if (this.formState.startDate) payload.startDate = this.formState.startDate;
        if (this.formState.endDate) payload.endDate = this.formState.endDate;
        if (this.isEdit) payload.id = this.formState.id;

        const res = this.isEdit ? await UpdatePromotion(payload) : await AddPromotion(payload);

        if (res.errCode !== 0) {
          this.$message.error(res.errMsg || "保存失败");
          return;
        }

        const id = payload.id !== undefined ? payload.id : (res.data?.id ?? res.data?.promotionId);


        this.$message.success(this.isEdit ? "修改成功" : "新增成功");
        this.$emit("ok", { id, isGlobal: payload.isGlobal });
        this.closeModal();
      } catch (error) {
        console.error("save promotion error:", error);
        this.$message.error("系统异常，请稍后重试");
      } finally {
        this.loading = false;
      }
    },

    closeModal() {
      this.$emit("update:visible", false);
      this.resetForm();
    },
  },
};
</script>

<style scoped>
.form-tip {
  color: #999;
  font-size: 12px;
  line-height: 1.6;
  margin-top: 4px;
}
</style>